import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Thermometer, Snowflake, Plus, Loader2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function TemperaturesPage() {
  const { locationId } = useAuth();
  const user = useUser();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [values, setValues] = useState({});
  const [corrective, setCorrective] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [eqForm, setEqForm] = useState({ name: "", kind: "fridge", placement: "", min_temp: 0, max_temp: 5 });
  const canManage = ["owner", "manager"].includes(user?.role);

  const load = useCallback(() => {
    const params = { location_id: locationId };
    api.get("/temperatures/latest", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/temperatures", { params: { ...params, limit: 30 } }).then((r) => setHistory(r.data)).catch(() => {});
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [load]);

  const submit = async (eq) => {
    const temp = Number(values[eq.equipment_id]);
    if (values[eq.equipment_id] === "" || isNaN(temp)) return;
    setBusyId(eq.equipment_id);
    try {
      const r = await api.post("/temperatures", {
        equipment_id: eq.equipment_id, temperature: temp,
        corrective_action: corrective[eq.equipment_id] || "", location_id: locationId,
      });
      if (r.data.out_of_range) {
        toast.error(`${eq.name}: ${temp}°C — ${t("temps.outOfRange")}`, { duration: 6000 });
      } else {
        toast.success(`${eq.name}: ${temp}°C`);
      }
      setValues({ ...values, [eq.equipment_id]: "" });
      setCorrective({ ...corrective, [eq.equipment_id]: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusyId(null);
  };

  const addEquipment = async () => {
    try {
      await api.post("/equipment", { ...eqForm, min_temp: Number(eqForm.min_temp), max_temp: Number(eqForm.max_temp), location_id: locationId });
      toast.success(t("common.success"));
      setDialog(false);
      setEqForm({ name: "", kind: "fridge", placement: "", min_temp: 0, max_temp: 5 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isOut = (eq) => {
    const v = Number(values[eq.equipment_id]);
    return values[eq.equipment_id] !== "" && !isNaN(v) && (v < eq.min_temp || v > eq.max_temp);
  };

  return (
    <div className="space-y-6 md:pl-14" data-testid="temperatures-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("temps.title")}</h1>
        {canManage && (
          <Button data-testid="add-equipment-button" variant="outline" onClick={() => setDialog(true)} className="h-11 tactile-btn">
            <Plus className="h-4 w-4 mr-1" />{t("temps.addEquipment")}
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="equipment-grid">
        {items.map(({ equipment: eq, latest }) => {
          const out = isOut(eq);
          return (
            <div key={eq.equipment_id} data-testid={`equipment-card-${eq.equipment_id}`}
              className={`bg-card rounded-2xl border-2 p-5 space-y-3 ${latest?.out_of_range ? "border-red-300 bg-red-50/40" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${eq.kind === "freezer" ? "bg-sky-600" : "bg-cyan-600"}`}>
                    {eq.kind === "freezer" ? <Snowflake className="h-5 w-5 text-white" /> : <Thermometer className="h-5 w-5 text-white" />}
                  </div>
                  <div>
                    <h2 className="font-display font-bold">{eq.name}</h2>
                    <p className="text-xs text-muted-foreground">{eq.placement} · {t("temps.range")} {eq.min_temp}° / {eq.max_temp}°C</p>
                  </div>
                </div>
                {latest && (
                  <div className="text-right">
                    <p className={`font-mono text-xl font-bold ${latest.out_of_range ? "text-red-600" : "text-emerald-600"}`}>
                      {latest.temperature > 0 ? "+" : ""}{latest.temperature}°C
                    </p>
                    <p className="text-[10px] text-muted-foreground">{format(new Date(latest.recorded_at), "dd/MM HH:mm")}</p>
                  </div>
                )}
                {!latest && <p className="text-xs text-muted-foreground">{t("temps.noReading")}</p>}
              </div>

              {out && (
                <div className="flex items-start gap-2 bg-red-100 border border-red-300 rounded-xl p-3" data-testid={`temp-alert-${eq.equipment_id}`}>
                  <AlertTriangle className="h-4 w-4 text-red-700 shrink-0 mt-0.5" />
                  <div className="text-xs text-red-800">
                    <p className="font-bold">{t("temps.alertTitle")}</p>
                    <p>{eq.name}: {values[eq.equipment_id]}°C</p>
                  </div>
                </div>
              )}

              {out && (
                <Input data-testid={`temp-corrective-${eq.equipment_id}`} placeholder={t("temps.corrective")}
                  value={corrective[eq.equipment_id] || ""}
                  onChange={(e) => setCorrective({ ...corrective, [eq.equipment_id]: e.target.value })} className="h-11" />
              )}

              <div className="flex gap-2">
                <Input data-testid={`temp-input-${eq.equipment_id}`} type="number" step="0.1" placeholder="°C"
                  value={values[eq.equipment_id] || ""}
                  onChange={(e) => setValues({ ...values, [eq.equipment_id]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && submit(eq)}
                  className="h-14 text-2xl font-mono text-center flex-1" />
                <Button data-testid={`temp-submit-${eq.equipment_id}`} onClick={() => submit(eq)}
                  disabled={busyId === eq.equipment_id || values[eq.equipment_id] === ""}
                  className={`h-14 px-6 tactile-btn font-bold ${out ? "bg-red-600 hover:bg-red-700" : ""}`}>
                  {busyId === eq.equipment_id ? <Loader2 className="h-5 w-5 animate-spin" /> : t("temps.record")}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-card border rounded-2xl p-5" data-testid="temperature-history">
        <h2 className="font-display font-bold mb-3">{t("temps.history")}</h2>
        <div className="space-y-2">
          {history.map((r) => (
            <div key={r.temperature_id} data-testid={`temp-record-${r.temperature_id}`}
              className="flex items-center gap-3 flex-wrap border-b last:border-0 pb-2">
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${r.out_of_range ? "bg-red-600" : "bg-emerald-600"}`} />
              <p className="font-semibold text-sm flex-1 min-w-28">{r.equipment_name}</p>
              <p className={`font-mono text-sm font-bold ${r.out_of_range ? "text-red-600" : "text-emerald-700"}`}>
                {r.temperature > 0 ? "+" : ""}{r.temperature}°C
              </p>
              <p className="text-xs text-muted-foreground">{r.user_name}</p>
              <p className="text-xs text-muted-foreground font-mono">{format(new Date(r.recorded_at), "dd/MM/yyyy HH:mm")}</p>
              {r.corrective_action && (
                <span className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary w-full sm:w-auto">{t("temps.corrective")}: {r.corrective_action}</span>
              )}
              <button data-testid={`temp-delete-${r.temperature_id}`} title={t("common.delete")}
                onClick={async () => {
                  if (!window.confirm(t("common.confirmDelete"))) return;
                  try { await api.delete(`/temperatures/${r.temperature_id}`); toast.success(t("common.success")); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}
                className="tactile-btn h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!history.length && <p className="text-sm text-muted-foreground">{t("temps.noReading")}</p>}
        </div>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-sm" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("temps.addEquipment")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("products.name")}</Label>
              <Input data-testid="equipment-name" value={eqForm.name} onChange={(e) => setEqForm({ ...eqForm, name: e.target.value })} className="h-11" placeholder="Frigo 3" />
            </div>
            <div className="space-y-1">
              <Label>{t("products.category")}</Label>
              <select data-testid="equipment-kind" value={eqForm.kind} onChange={(e) => setEqForm({ ...eqForm, kind: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                <option value="fridge">Frigo</option>
                <option value="freezer">Freezer</option>
                <option value="cella">Cella frigo</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("products.location")}</Label>
              <Input data-testid="equipment-placement" value={eqForm.placement} onChange={(e) => setEqForm({ ...eqForm, placement: e.target.value })} className="h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Min °C</Label>
                <Input data-testid="equipment-min" type="number" step="0.5" value={eqForm.min_temp} onChange={(e) => setEqForm({ ...eqForm, min_temp: e.target.value })} className="h-11" />
              </div>
              <div className="space-y-1">
                <Label>Max °C</Label>
                <Input data-testid="equipment-max" type="number" step="0.5" value={eqForm.max_temp} onChange={(e) => setEqForm({ ...eqForm, max_temp: e.target.value })} className="h-11" />
              </div>
            </div>
            <Button data-testid="equipment-save" onClick={addEquipment} disabled={!eqForm.name} className="w-full h-12 tactile-btn font-bold">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
